import React, { HTMLAttributes, ReactNode } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import ImageSkeleton, { ImageSkeletonProps } from "components/skeletons/ImageSkeleton";
import descriptionBackground from "assets/images/homePage/gameInfoSection/featuresTile/features_bg_zigzag.jpg";

import jaggyTop from "assets/images/common/yellowJaggyEdges/yellow_jaggy_top.png";
import jaggyBottom from "assets/images/common/yellowJaggyEdges/yellow_jaggy_bottom.png";
import Spacer from "components/Spacer";
import { featureTileYellow } from "theme";

function getDescriptionBackgroundPosition(
    descriptionBackgroundVariant: TextBoxWithImageDescriptionBackgroundVariant
) {
    if (descriptionBackgroundVariant === "v1") return "top";
    if (descriptionBackgroundVariant === "v2") return "center";
    if (descriptionBackgroundVariant === "v3") return "bottom";

    return "top";
}

interface IStylesProps {
    mobile: boolean;
    descriptionBackgroundVariant: TextBoxWithImageDescriptionBackgroundVariant;
}

const useStyles = (props: IStylesProps) =>
    makeStyles((theme: Theme) => {
        const { mobile, descriptionBackgroundVariant } = props;

        const horizontalPadding = theme.spacing(1);
        const gutterPadding = theme.spacing(3);

        const getImageBorder = (size: number) => `${size}px solid ${featureTileYellow}`;

        const imageBorder = mobile ? getImageBorder(5) : getImageBorder(8);

        return createStyles({
            root: {
                display: "flex",
                flexDirection: mobile ? "column" : "row",
            },
            image: {
                display: "flex",
                alignItems: "center",
                border: imageBorder,
                borderBottom: mobile ? 0 : imageBorder,
                position: "relative",
                flex: mobile ? "unset" : 2,
            },
            titleContainer: {
                padding: `${horizontalPadding}px ${gutterPadding}px`,
                backgroundColor: featureTileYellow,
            },
            title: {
                lineHeight: 1,
            },
            desktopHeading: {
                color: featureTileYellow,
            },
            descriptionContainer: {
                display: "flex",
                flex: 1,
                color: theme.palette.common.white,
                backgroundImage: `url(${descriptionBackground})`,
                backgroundColor: "#0E0E10",
                position: "relative",
                backgroundSize: "100% auto",
                backgroundPositionX: "center",
                backgroundPositionY: getDescriptionBackgroundPosition(descriptionBackgroundVariant),
            },
            descriptionContainerContent: {
                padding: `${horizontalPadding}px ${gutterPadding}px`,
                flex: 1,
            },
            description: {
                color: theme.palette.common.white,
                lineHeight: 1.35,
            },
        });
    });

export type TextBoxWithImageDescriptionBackgroundVariant = "v1" | "v2" | "v3";
type IClasses = Partial<ReturnType<ReturnType<typeof useStyles>>>;

interface IProps extends HTMLAttributes<HTMLDivElement> {
    heading?: ReactNode; // TODO: what if it's undefined?
    imageProps: ImageSkeletonProps;
    descriptionBackgroundVariant?: TextBoxWithImageDescriptionBackgroundVariant;
    mobile?: boolean;
    // loading?: boolean;
    // hoverable?: boolean;
    classes?: IClasses;
}

export default function TextBoxWithImage(props: IProps) {
    const {
        className,
        heading,
        imageProps,
        descriptionBackgroundVariant = "v1",
        children,
        mobile = false,
        classes: overrideClasses,
        ...rest
    } = props;

    const classes = useStyles({ mobile, descriptionBackgroundVariant })();

    const headingComponent =
        typeof heading === "string" ? (
            <Typography className={clsx(classes.title, overrideClasses?.title)} variant="h5">
                {heading}
            </Typography>
        ) : (
            heading
        );

    return (
        <div className={clsx(className, classes.root, overrideClasses?.root)} {...rest}>
            <div className={clsx(classes.image, overrideClasses?.image)}>
                <ImageSkeleton {...imageProps} />
                {mobile ? (
                    <img
                        src={jaggyTop}
                        alt=""
                        style={{ width: "100%", position: "absolute", bottom: -2, left: 0 }}
                    />
                ) : (
                    <>
                        <img
                            src={jaggyBottom}
                            alt=""
                            style={{ width: "100%", position: "absolute", top: -3, right: 0 }}
                        />
                        <img
                            src={jaggyTop}
                            alt=""
                            style={{ width: "90%", position: "absolute", bottom: -5, right: 0 }}
                        />
                    </>
                )}
            </div>

            {mobile ? (
                <div className={clsx(classes.titleContainer, overrideClasses?.titleContainer)}>
                    {headingComponent}
                </div>
            ) : null}

            <div
                className={clsx(
                    classes.descriptionContainer,
                    overrideClasses?.descriptionContainer
                )}
            >
                {mobile ? (
                    <img
                        src={jaggyBottom}
                        alt=""
                        style={{ width: "100%", position: "absolute", top: -2, left: 0 }}
                    />
                ) : null}

                {mobile && typeof children === "undefined" ? null : (
                    <div
                        className={clsx(
                            classes.descriptionContainerContent,
                            overrideClasses?.descriptionContainerContent
                        )}
                    >
                        {mobile ? (
                            <Spacer />
                        ) : (
                            <>
                                <div
                                    className={clsx(
                                        classes.desktopHeading,
                                        overrideClasses?.desktopHeading
                                    )}
                                >
                                    {headingComponent}
                                </div>
                                <Spacer size={2} />
                            </>
                        )}
                        {typeof children === "string" ? (
                            <Typography
                                className={clsx(classes.description, overrideClasses?.description)}
                            >
                                {children}
                            </Typography>
                        ) : (
                            children
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
